import { useSelector } from 'react-redux';
import { ClientServices } from './components/ClientServices.tsx';
import { NACEInfo } from './components/NACEInfo.tsx';
import LicenseExistence from '../../components/FormComponents/LicenseExistence/LicenseExistence.tsx';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { styles } from './styles.ts';
import NonProfitableOrg from './components/NonProfitableOrg/NonProfitableOrg.tsx';
import { EssenceOfActivity } from './components/EssenceOfActivity/EssenceOfActivity.tsx';
import { ConflictTeretories } from './components/ConflictTeretories/ConflictTeretories.tsx';
import useGetContragentTypes from '../../hooks/useGetContragentTypes.ts';
import { useBusinessRelationsForm } from './hooks/useBusinessRelationsForm.ts';
import { FieldErrors, FormProvider } from 'react-hook-form';
import {
  BUSINESS_RELATIONS_LEGAL_RESIDENT_TEXT,
  BUSINESS_RELATIONS_SOLE_TRADER_TEXT,
  testDataBusinessRelationsLegalResident,
  testDataBusinessRelationsSoleTrader,
} from './constants.ts';
import { useScrollToError } from './hooks/useScrollToError.ts';
import { TPurposeOfBusinessRelations } from './types.ts';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.ts';
import { cloneDeep } from 'lodash';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { styles as root } from '../../styles.ts';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';

interface IBusinessRelationsProps {
  onNextStep: () => void;
}

export default function BusinessRelations({ onNextStep }: IBusinessRelationsProps) {
  const dispatch = useAppDispatch();
  const { isSoleTrader } = useGetContragentTypes();
  const { isTestEnvironmentEnabled } = useFeatureFlags();
  const form = useBusinessRelationsForm();

  const testData = isSoleTrader
    ? (testDataBusinessRelationsSoleTrader as unknown as TPurposeOfBusinessRelations)
    : (testDataBusinessRelationsLegalResident as unknown as TPurposeOfBusinessRelations);

  const handleFillTestData = () => form.reset(testData, { keepDefaultValues: true });

  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);

  const { isSubmitting, isDirty } = form.formState;

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TPurposeOfBusinessRelations) => {
      if (isDirty) {
        try {
          const data = {
            purposeOfBusinessRelations: values as TPurposeOfBusinessRelations,
            applicationId: appId as number,
          };
          await dispatch(
            updateForm({ data, reportId: IdentificationFormSteps.purposeOfBusinessRelations })
          );
          const formData = {
            field:
              IdentificationFormStepNames.purposeOfBusinessRelations as keyof TIdentificationForm,
            data: cloneDeep(values),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  const titles = isSoleTrader
    ? BUSINESS_RELATIONS_SOLE_TRADER_TEXT
    : BUSINESS_RELATIONS_LEGAL_RESIDENT_TEXT;

  useScrollToError(
    form.formState.isSubmitted,
    form.formState.errors as FieldErrors<TPurposeOfBusinessRelations>
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        {isTestEnvironmentEnabled && (
          <Box sx={root.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        {isReidentification && (
          <Box sx={{ mb: 2 }}>
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          </Box>
        )}
        <Box sx={{ ...commonStyles.columnFlexContainer, gap: 5 }}>
          <Paper sx={styles.paperContainer}>
            <NACEInfo tooltipTitle={titles.textTooltipBusinessDescription} />
            {!isSoleTrader && (
              <>
                <LicenseExistence isLicensedFieldName="isLicensed" />
                <NonProfitableOrg />
              </>
            )}
            <ConflictTeretories premisesInConflictZoneText={titles.isPremisesInConflictZoneText} />
            <EssenceOfActivity />
          </Paper>
          <Box sx={{ width: '100%' }}>
            <Divider sx={root.divider} />
          </Box>
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            {titles.serviceHeaderText}
          </Typography>

          <ClientServices />
          <Button
            type="submit"
            sx={{ alignSelf: 'center' }}
            variant="contained"
            disabled={isSubmitting}
          >
            Продовжити
          </Button>
        </Box>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </form>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
}
